import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { can, isReadOnlyForLot } from "@/lib/permissions";
import {
  ZoomIn, ZoomOut, Maximize, MousePointer2, MapPin, Square, Hexagon, Slash,
  ArrowUpRight, Type, Highlighter, Ruler, Trash2, Eye, EyeOff, Save,
  ChevronLeft, ChevronRight, X, Circle as CircleIcon, PenLine, Move3D, AlertTriangle,
} from "lucide-react";

const TOOLS_BASE = [
  { id: "select",       icon: MousePointer2, label: "Select / Pan" },
  { id: "polygon",      icon: Hexagon,       label: "Polygon area" },
  { id: "edit-polygon", icon: Move3D,        label: "Edit polygon vertices" },
  { id: "rectangle",    icon: Square,        label: "Rectangle" },
  { id: "circle",       icon: CircleIcon,    label: "Circle / Ellipse" },
  { id: "pin",          icon: MapPin,        label: "Pin" },
  { id: "line",         icon: Slash,         label: "Line / Chainage" },
  { id: "arrow",        icon: ArrowUpRight,  label: "Arrow" },
  { id: "text",         icon: Type,          label: "Text Note" },
  { id: "highlight",    icon: Highlighter,   label: "Highlight" },
  { id: "freehand",     icon: PenLine,       label: "Freehand pen" },
  { id: "set-scale",    icon: Ruler,         label: "Set Scale (admin)" },
];

// Format a numeric m² area to 2 dp, with thousands separator.
const fmtArea = (a) => {
  if (a == null || isNaN(a)) return null;
  return `${Number(a).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`;
};

export default function PlanViewer({ readOnly: rOverride, token: tokenOverride, planIdOverride }) {
  const params = useParams();
  const planId = planIdOverride || params.id;
  const nav = useNavigate();
  const { user } = useAuth();

  const [plan, setPlan] = useState(null);
  const [markups, setMarkups] = useState([]);
  const [linkedLots, setLinkedLots] = useState([]);
  const [tool, setTool] = useState("select");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [draft, setDraft] = useState(null);                 // in-progress markup
  const [selected, setSelected] = useState(null);
  const [showMarkups, setShowMarkups] = useState(true);
  const [pageNum, setPageNum] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [pdfRender, setPdfRender] = useState(null);          // { url, width, height }
  const [imgDims, setImgDims] = useState(null);              // {w,h} natural
  const [saveDialog, setSaveDialog] = useState(null);        // {tool, points}
  const [scaleDialog, setScaleDialog] = useState(null);      // {pointA, pointB}
  const [supersedeWarn, setSupersedeWarn] = useState(false);
  const [editingPolyId, setEditingPolyId] = useState(null);  // markup id when Edit Polygon mode is active
  const [mobileSheet, setMobileSheet] = useState(false);     // mobile properties bottom sheet

  const isAdminOrQa = ["admin", "qa_manager"].includes(user?.role);
  const roleReadOnly = !!tokenOverride || isReadOnlyForLot(user?.role) || !can.editMarkup(user?.role);
  const readOnly = rOverride || roleReadOnly;

  const fileUrl = useMemo(() => {
    if (!planId) return null;
    if (tokenOverride) return `${API}/plans/${planId}/file?token=${tokenOverride}`;
    return `${API}/plans/${planId}/file`;
  }, [planId, tokenOverride]);

  const isPdf = (plan?.file_type || "").includes("pdf");
  const calibration = plan?.scale_calibration;
  const isCalibrated = !!(calibration && calibration.pixels_per_meter);

  const reloadMarkups = useCallback(async () => {
    if (tokenOverride) {
      const r = await fetch(`${API}/public/plans/${planId}/markups?token=${tokenOverride}`);
      if (r.ok) setMarkups(await r.json());
    } else {
      const r = await api.get("/markups", { params: { plan_id: planId } });
      setMarkups(r.data);
    }
  }, [planId, tokenOverride]);

  const reloadPlan = useCallback(async () => {
    if (tokenOverride) { setPlan({ id: planId, file_type: "" }); return; }
    const r = await api.get(`/plans/${planId}`);
    setPlan(r.data);
    if (r.data?.status === "Superseded") setSupersedeWarn(true);
  }, [planId, tokenOverride]);

  useEffect(() => {
    reloadPlan();
    if (!tokenOverride) api.get(`/plans/${planId}/lots`).then(r => setLinkedLots(r.data));
    reloadMarkups();
  }, [planId, tokenOverride, reloadMarkups, reloadPlan]);

  // Render PDF page via pdfjs-dist
  useEffect(() => {
    if (!fileUrl || !plan) return;
    const detectAndRender = async () => {
      try {
        const headers = {};
        if (!tokenOverride) {
          const tok = localStorage.getItem("token");
          if (tok) headers["Authorization"] = `Bearer ${tok}`;
        }
        const probe = await fetch(fileUrl, { headers });
        if (!probe.ok) throw new Error(`File fetch failed (HTTP ${probe.status})`);
        const ctype = probe.headers.get("content-type") || "";
        const isPdfNow = ctype.includes("pdf");
        if (!isPdfNow) {
          const blob = await probe.blob();
          const blobUrl = URL.createObjectURL(blob);
          setPdfRender({ url: blobUrl, width: 0, height: 0, _isImage: true });
          setPlan(p => ({ ...p, file_type: ctype }));
          return;
        }
        setPlan(p => ({ ...p, file_type: ctype }));
        const buf = await probe.arrayBuffer();
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        try {
          pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
        } catch {
          pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }
        const doc = await pdfjs.getDocument({ data: buf }).promise;
        setPageCount(doc.numPages);
        const page = await doc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        setPdfRender({ url: canvas.toDataURL("image/png"), width: viewport.width, height: viewport.height });
      } catch (e) {
        console.error("PDF render failed", e);
        toast.error("Unable to open this file. Please download it or contact admin.");
      }
    };
    detectAndRender();
  }, [fileUrl, pageNum, plan?.id, tokenOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const onImgLoad = (e) => setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });

  // The base render dims used for calibration: PDF render dims, or image natural dims.
  const baseDims = useMemo(() => {
    if (isPdf && pdfRender?.width) return { w: pdfRender.width, h: pdfRender.height };
    if (imgDims) return { w: imgDims.w, h: imgDims.h };
    return null;
  }, [isPdf, pdfRender, imgDims]);

  const containerRef = useRef(null);
  const svgRef = useRef(null);

  const toNormCoords = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  };

  // -------------------- Canvas event handlers --------------------
  const dragRef = useRef(null);     // pan / vertex / pinch state
  const freehandRef = useRef(null); // active freehand draft buffer

  const onCanvasPointerDown = (ev) => {
    if (readOnly) return;
    // Freehand: start a stream
    if (tool === "freehand") {
      const p = toNormCoords(ev.clientX, ev.clientY);
      if (!p) return;
      freehandRef.current = { tool: "freehand", points: [p] };
      setDraft(freehandRef.current);
      ev.currentTarget.setPointerCapture?.(ev.pointerId);
      return;
    }
    // Select tool: start pan
    if (tool === "select") {
      dragRef.current = { kind: "pan", x: ev.clientX, y: ev.clientY, px: pan.x, py: pan.y };
    }
  };

  const onCanvasPointerMove = (ev) => {
    if (tool === "freehand" && freehandRef.current) {
      const p = toNormCoords(ev.clientX, ev.clientY);
      if (!p) return;
      // Throttle: only add point if it's > a small delta from last.
      const last = freehandRef.current.points[freehandRef.current.points.length - 1];
      const dx = p.x - last.x, dy = p.y - last.y;
      if (dx * dx + dy * dy > 0.00002) {
        freehandRef.current.points.push(p);
        setDraft({ ...freehandRef.current, points: [...freehandRef.current.points] });
      }
      return;
    }
    if (dragRef.current?.kind === "pan") {
      setPan({ x: dragRef.current.px + (ev.clientX - dragRef.current.x), y: dragRef.current.py + (ev.clientY - dragRef.current.y) });
    }
  };

  const onCanvasPointerUp = () => {
    if (tool === "freehand" && freehandRef.current) {
      const pts = freehandRef.current.points;
      freehandRef.current = null;
      if (pts.length >= 2) finishDraft(pts);
      else setDraft(null);
      return;
    }
    dragRef.current = null;
  };

  const onCanvasClick = (ev) => {
    if (readOnly) return;
    if (tool === "select" || tool === "edit-polygon" || tool === "freehand") return;
    const p = toNormCoords(ev.clientX, ev.clientY);
    if (!p) return;

    if (tool === "set-scale") {
      const current = draft && draft.tool === "set-scale" ? draft.points : [];
      const next = [...current, p];
      if (next.length === 2) {
        setScaleDialog({ pointA: next[0], pointB: next[1] });
        setDraft(null);
      } else {
        setDraft({ tool: "set-scale", points: next });
      }
      return;
    }
    if (tool === "pin" || tool === "text") {
      finishDraft([p]);
      return;
    }
    if (tool === "rectangle" || tool === "circle" || tool === "line" ||
        tool === "arrow" || tool === "highlight") {
      const current = draft && draft.tool === tool ? draft.points : [];
      const next = [...current, p];
      if (next.length === 2) finishDraft(next);
      else setDraft({ tool, points: next });
      return;
    }
    if (tool === "polygon") {
      const current = draft && draft.tool === "polygon" ? draft.points : [];
      setDraft({ tool: "polygon", points: [...current, p] });
    }
  };

  const finishDraft = (points) => {
    setSaveDialog({ tool, points, label: "", description: "" });
    setDraft(null);
  };

  const finishPolygon = () => {
    if (!draft || draft.tool !== "polygon" || draft.points.length < 3) {
      toast.error("Polygon needs at least 3 points"); return;
    }
    finishDraft(draft.points);
  };

  const cancelDraft = () => { setDraft(null); setSaveDialog(null); setScaleDialog(null); };

  // -------------------- Vertex drag (Edit Polygon mode) --------------------
  const dragVertexRef = useRef(null);
  const onVertexDragStart = (markupId, vertexIdx) => (ev) => {
    if (readOnly || tool !== "edit-polygon" || editingPolyId !== markupId) return;
    ev.stopPropagation();
    ev.preventDefault();
    dragVertexRef.current = { markupId, vertexIdx };
    document.addEventListener("pointermove", onVertexDragMove);
    document.addEventListener("pointerup", onVertexDragEnd);
  };
  const onVertexDragMove = (ev) => {
    if (!dragVertexRef.current) return;
    const p = toNormCoords(ev.clientX, ev.clientY);
    if (!p) return;
    const { markupId, vertexIdx } = dragVertexRef.current;
    setMarkups(curr => curr.map(m => {
      if (m.id !== markupId) return m;
      const coords = [...m.coordinates];
      coords[vertexIdx] = p;
      return { ...m, coordinates: coords };
    }));
  };
  const onVertexDragEnd = async () => {
    document.removeEventListener("pointermove", onVertexDragMove);
    document.removeEventListener("pointerup", onVertexDragEnd);
    const drag = dragVertexRef.current;
    dragVertexRef.current = null;
    if (!drag) return;
    const m = markups.find(x => x.id === drag.markupId);
    if (!m) return;
    try {
      const { data } = await api.put(`/markups/${drag.markupId}`, { coordinates: m.coordinates });
      // The PUT returns {ok:true}; refetch to get the new area_m2 server-computed value.
      reloadMarkups();
    } catch (e) {
      toast.error("Failed to save vertex");
      reloadMarkups();
    }
  };

  const insertVertex = async (markupId, atIdx) => {
    const m = markups.find(x => x.id === markupId);
    if (!m) return;
    // Insert midpoint between atIdx and atIdx+1 (wrap)
    const a = m.coordinates[atIdx];
    const b = m.coordinates[(atIdx + 1) % m.coordinates.length];
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const next = [...m.coordinates.slice(0, atIdx + 1), mid, ...m.coordinates.slice(atIdx + 1)];
    setMarkups(curr => curr.map(x => x.id === markupId ? { ...x, coordinates: next } : x));
    try { await api.put(`/markups/${markupId}`, { coordinates: next }); reloadMarkups(); }
    catch { toast.error("Failed to insert vertex"); reloadMarkups(); }
  };

  const deleteVertex = async (markupId, atIdx) => {
    const m = markups.find(x => x.id === markupId);
    if (!m || m.coordinates.length <= 3) { toast.error("A polygon must have at least 3 vertices"); return; }
    const next = m.coordinates.filter((_, i) => i !== atIdx);
    setMarkups(curr => curr.map(x => x.id === markupId ? { ...x, coordinates: next } : x));
    try { await api.put(`/markups/${markupId}`, { coordinates: next }); reloadMarkups(); }
    catch { toast.error("Failed to delete vertex"); reloadMarkups(); }
  };

  // -------------------- Misc --------------------
  const deleteMarkup = async (id) => {
    if (!window.confirm("Delete this markup?")) return;
    try {
      await api.delete(`/markups/${id}`);
      toast.success("Deleted");
      setSelected(null);
      if (editingPolyId === id) { setEditingPolyId(null); setTool("select"); }
      reloadMarkups();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clearCalibration = async () => {
    if (!window.confirm("Clear scale calibration? Polygon areas will become unavailable until a new scale is set.")) return;
    try {
      await api.delete(`/plans/${planId}/calibrate-scale`);
      toast.success("Scale cleared");
      reloadPlan();
      reloadMarkups();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onMarkupClick = (m, ev) => {
    ev.stopPropagation();
    if (tool === "edit-polygon" && m.markup_type === "polygon") {
      setEditingPolyId(m.id);
      setSelected(m);
      return;
    }
    if (tool !== "select") return;
    setSelected(m);
    setMobileSheet(true);
  };

  const zoomIn = () => setZoom(z => Math.min(8, z * 1.25));
  const zoomOut = () => setZoom(z => Math.max(0.2, z / 1.25));
  const fit = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current?.requestFullscreen?.();
  };

  // -------------------- Touch / pinch zoom on the container --------------------
  const touchRef = useRef(null);
  const onTouchStart = (ev) => {
    if (ev.touches.length === 2) {
      const [a, b] = ev.touches;
      const dx = a.clientX - b.clientX, dy = a.clientY - b.clientY;
      touchRef.current = { kind: "pinch", startDist: Math.hypot(dx, dy), startZoom: zoom };
    } else if (ev.touches.length === 1 && tool === "select") {
      const t = ev.touches[0];
      touchRef.current = { kind: "pan", x: t.clientX, y: t.clientY, px: pan.x, py: pan.y };
    }
  };
  const onTouchMove = (ev) => {
    if (!touchRef.current) return;
    if (touchRef.current.kind === "pinch" && ev.touches.length === 2) {
      const [a, b] = ev.touches;
      const dx = a.clientX - b.clientX, dy = a.clientY - b.clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / touchRef.current.startDist;
      setZoom(Math.max(0.2, Math.min(8, touchRef.current.startZoom * ratio)));
      ev.preventDefault();
    } else if (touchRef.current.kind === "pan" && ev.touches.length === 1) {
      const t = ev.touches[0];
      setPan({
        x: touchRef.current.px + (t.clientX - touchRef.current.x),
        y: touchRef.current.py + (t.clientY - touchRef.current.y),
      });
    }
  };
  const onTouchEnd = () => { touchRef.current = null; };

  if (!plan) return <div className="p-8 text-slate-500">Loading plan…</div>;

  const imageSrc = pdfRender?.url || (isPdf ? null : fileUrl);
  const aspect = isPdf && pdfRender ? pdfRender.width / pdfRender.height : (imgDims ? imgDims.w / imgDims.h : 16 / 9);

  // Visible tools depend on role + mode.
  const TOOLS = TOOLS_BASE.filter(t => {
    if (t.id === "set-scale") return isAdminOrQa && !tokenOverride;
    return true;
  });

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col lg:h-screen" data-testid="plan-viewer">
      {/* ===================== Top bar / breadcrumb / status ===================== */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          {!tokenOverride && <Link to="/plans" className="text-xs text-slate-500 hover:underline">← Plans</Link>}
          <div className="ml-2 min-w-0">
            <div className="overline text-teal-700">{plan.drawing_number} · Rev {plan.revision}</div>
            <div className="truncate font-heading text-sm font-bold text-slate-900">{plan.title}</div>
          </div>
          {plan.status && <StatusBadge status={plan.status} className="ml-2"/>}
          {readOnly && !tokenOverride && (
            <span className="ml-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-700">View only</span>
          )}
        </div>
        <ToolbarActions
          tools={TOOLS}
          tool={tool} setTool={setTool}
          zoomIn={zoomIn} zoomOut={zoomOut} fit={fit} fullscreen={fullscreen}
          showMarkups={showMarkups} setShowMarkups={setShowMarkups}
          pageCount={pageCount} pageNum={pageNum} setPageNum={setPageNum}
          isPdf={isPdf} finishPolygon={finishPolygon} draft={draft} cancelDraft={cancelDraft}
          readOnly={readOnly}
        />
      </div>

      {/* ===================== Calibration banner ===================== */}
      <CalibrationBanner
        plan={plan}
        isCalibrated={isCalibrated}
        canCalibrate={isAdminOrQa && !tokenOverride}
        onSet={() => setTool("set-scale")}
        onClear={clearCalibration}
      />

      {/* ===================== Canvas + sidebar ===================== */}
      <div className="flex flex-1 overflow-hidden">
        <div
          ref={containerRef}
          className="relative flex-1 overflow-hidden bg-slate-200 touch-none"
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={onCanvasPointerUp}
          onPointerCancel={onCanvasPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          data-testid="plan-canvas"
        >
          {imageSrc ? (
            <div
              className="absolute left-1/2 top-1/2 origin-center select-none"
              style={{ transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
            >
              <div className="relative" style={{ width: "min(85vw, 1400px)", aspectRatio: aspect }}>
                <img
                  src={imageSrc}
                  alt={plan.title}
                  className="size-full object-contain"
                  draggable={false}
                  onLoad={onImgLoad}
                  data-testid="plan-image"
                />
                <svg
                  ref={svgRef}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  className="absolute inset-0 size-full"
                  style={{ cursor: tool === "select" ? "grab" : "crosshair", pointerEvents: "auto" }}
                  onClick={onCanvasClick}
                >
                  {showMarkups && markups.filter(m => (m.page || 1) === pageNum).map(m => (
                    <RenderMarkup
                      key={m.id} m={m}
                      selected={selected?.id === m.id}
                      onClick={(e) => onMarkupClick(m, e)}
                    />
                  ))}
                  {/* Edit-polygon vertex handles overlay */}
                  {!readOnly && tool === "edit-polygon" && editingPolyId && (() => {
                    const m = markups.find(x => x.id === editingPolyId);
                    if (!m || m.markup_type !== "polygon") return null;
                    return (
                      <VertexHandles
                        markup={m}
                        onVertexDragStart={onVertexDragStart}
                        onInsertVertex={insertVertex}
                        onDeleteVertex={deleteVertex}
                      />
                    );
                  })()}
                  {draft && <RenderDraft draft={draft}/>}
                </svg>
              </div>
            </div>
          ) : (
            <div className="grid h-full place-items-center text-slate-500">Rendering plan…</div>
          )}
        </div>

        {/* Desktop sidebar */}
        {!readOnly && (
          <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-slate-200 bg-white p-3 lg:block" data-testid="plan-sidebar">
            <SidebarPanel
              selected={selected} setSelected={setSelected}
              markups={markups} linkedLots={linkedLots} plan={plan}
              reloadMarkups={reloadMarkups}
              onDelete={deleteMarkup}
              editingPolyId={editingPolyId} setEditingPolyId={setEditingPolyId}
              setTool={setTool}
            />
          </aside>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {!readOnly && mobileSheet && selected && (
        <MobilePropertiesSheet
          selected={selected} setSelected={setSelected}
          plan={plan}
          onClose={() => setMobileSheet(false)}
          onDelete={() => deleteMarkup(selected.id)}
          onEdit={() => { setEditingPolyId(selected.id); setTool("edit-polygon"); setMobileSheet(false); }}
        />
      )}

      {saveDialog && (
        <SaveMarkupDialog
          open onClose={cancelDraft}
          tool={saveDialog.tool} points={saveDialog.points}
          plan={plan}
          onSaved={() => { setSaveDialog(null); reloadMarkups(); }}
        />
      )}

      {scaleDialog && (
        <ScaleCalibrationDialog
          open
          onClose={() => setScaleDialog(null)}
          planId={planId}
          pointA={scaleDialog.pointA} pointB={scaleDialog.pointB}
          baseDims={baseDims}
          onSaved={() => { setScaleDialog(null); reloadPlan(); reloadMarkups(); setTool("select"); }}
        />
      )}

      <SupersededWarningModal open={supersedeWarn} onClose={() => setSupersedeWarn(false)} plan={plan}/>
    </div>
  );
}

// =====================================================================================
// Toolbar
// =====================================================================================
function ToolbarActions({ tools, tool, setTool, zoomIn, zoomOut, fit, fullscreen, showMarkups, setShowMarkups, pageCount, pageNum, setPageNum, isPdf, finishPolygon, draft, cancelDraft, readOnly }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {!readOnly && tools.map(t => (
        <Button
          key={t.id} type="button" size="sm" variant={tool === t.id ? "default" : "outline"}
          onClick={() => setTool(t.id)} title={t.label} data-testid={`tool-${t.id}`}
          className={`h-9 min-w-9 px-2 sm:h-8 sm:min-w-8 ${tool === t.id ? "bg-teal-700 hover:bg-teal-800" : ""}`}
        >
          <t.icon className="size-4"/>
        </Button>
      ))}
      {!readOnly && tool === "polygon" && draft?.points?.length >= 3 && (
        <Button size="sm" onClick={finishPolygon} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finish-polygon">
          <Save className="mr-1 size-4"/>Finish ({draft.points.length})
        </Button>
      )}
      {!readOnly && draft && <Button size="sm" variant="ghost" onClick={cancelDraft} data-testid="cancel-draft"><X className="mr-1 size-4"/>Cancel</Button>}
      {!readOnly && <div className="mx-2 hidden h-6 w-px bg-slate-200 sm:block"/>}
      <Button size="sm" variant="outline" onClick={zoomOut} className="h-9 min-w-9 px-2 sm:h-8 sm:min-w-8" title="Zoom out" data-testid="zoom-out"><ZoomOut className="size-4"/></Button>
      <Button size="sm" variant="outline" onClick={zoomIn} className="h-9 min-w-9 px-2 sm:h-8 sm:min-w-8" title="Zoom in" data-testid="zoom-in"><ZoomIn className="size-4"/></Button>
      <Button size="sm" variant="outline" onClick={fit} className="h-9 px-2 sm:h-8" title="Fit to screen" data-testid="fit">Fit</Button>
      <Button size="sm" variant="outline" onClick={fullscreen} className="h-9 min-w-9 px-2 sm:h-8 sm:min-w-8" title="Fullscreen" data-testid="fullscreen"><Maximize className="size-4"/></Button>
      <Button size="sm" variant="outline" onClick={() => setShowMarkups(s => !s)} className="h-9 min-w-9 px-2 sm:h-8 sm:min-w-8" title="Toggle markups" data-testid="toggle-markups">
        {showMarkups ? <Eye className="size-4"/> : <EyeOff className="size-4"/>}
      </Button>
      {isPdf && pageCount > 1 && (
        <>
          <div className="mx-2 h-6 w-px bg-slate-200"/>
          <Button size="sm" variant="outline" onClick={() => setPageNum(n => Math.max(1, n - 1))} data-testid="page-prev"><ChevronLeft className="size-4"/></Button>
          <span className="px-2 text-xs">{pageNum}/{pageCount}</span>
          <Button size="sm" variant="outline" onClick={() => setPageNum(n => Math.min(pageCount, n + 1))} data-testid="page-next"><ChevronRight className="size-4"/></Button>
        </>
      )}
    </div>
  );
}

// =====================================================================================
// Calibration banner
// =====================================================================================
function CalibrationBanner({ plan, isCalibrated, canCalibrate, onSet, onClear }) {
  const cal = plan.scale_calibration;
  if (isCalibrated) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900" data-testid="calibration-banner-calibrated">
        <div className="flex items-center gap-2">
          <Ruler className="size-3.5"/>
          <span>
            <b>Scale calibrated.</b>{" "}
            1 m ≈ {Number(cal.pixels_per_meter).toFixed(1)} px (ref: {cal.real_distance} {cal.unit})
            {cal.calibrated_by_name && <> · by {cal.calibrated_by_name}</>}
          </span>
        </div>
        {canCalibrate && (
          <div className="flex gap-1">
            <button onClick={onSet} className="font-semibold text-emerald-800 hover:underline" data-testid="recalibrate-link">Re-calibrate</button>
            <span>·</span>
            <button onClick={onClear} className="font-semibold text-rose-700 hover:underline" data-testid="clear-calibration-link">Clear</button>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="calibration-banner-uncalibrated">
      <div className="flex items-center gap-2">
        <AlertTriangle className="size-3.5"/>
        <span><b>Scale not calibrated.</b> Polygon areas will not be calculated until scale is set.</span>
      </div>
      {canCalibrate && (
        <button onClick={onSet} className="font-semibold text-teal-800 hover:underline" data-testid="set-scale-link">Set scale</button>
      )}
    </div>
  );
}

// =====================================================================================
// Markup rendering (SVG)
// =====================================================================================
function RenderMarkup({ m, selected, onClick }) {
  const c = m.colour || "#0369a1";
  const opacity = selected ? 1 : 0.85;
  const stroke = selected ? "#0f172a" : c;
  const strokeW = m.stroke_width != null ? Number(m.stroke_width) : (selected ? 0.6 : 0.4);
  const dash = m.line_style === "dashed" ? "1.5 1" : m.line_style === "dotted" ? "0.3 0.6" : undefined;
  const fillOpacity = m.fill_opacity != null ? Number(m.fill_opacity) : 0.18;

  const pts = (m.coordinates || []).map(p => `${p.x * 100},${p.y * 100}`).join(" ");
  const center = m.coordinates?.[0] || { x: 0.5, y: 0.5 };
  const label = m.label || "";
  const area = fmtArea(m.area_m2);

  // For polygon: place area + label at centroid (approx avg of vertices)
  const centroid = (() => {
    if (m.markup_type !== "polygon") return center;
    const cs = m.coordinates || [];
    if (!cs.length) return center;
    const sx = cs.reduce((a, p) => a + p.x, 0) / cs.length;
    const sy = cs.reduce((a, p) => a + p.y, 0) / cs.length;
    return { x: sx, y: sy };
  })();

  const labelBlock = (() => {
    const lines = [];
    if (label) lines.push(label);
    if (m.markup_type === "polygon") lines.push(area || "(scale not set)");
    if (!lines.length) return null;
    const maxLen = Math.max(...lines.map(s => s.length));
    const x = centroid.x * 100, y = centroid.y * 100;
    return (
      <g style={{ pointerEvents: "none" }}>
        <rect
          x={x + 0.5} y={y - 3 - (lines.length - 1) * 2.2}
          rx={0.6} ry={0.6}
          width={Math.max(maxLen * 0.95, 5)} height={2.5 + (lines.length - 1) * 2.2}
          fill="white" opacity={0.92} stroke="#cbd5e1" strokeWidth="0.1"
        />
        {lines.map((s, i) => (
          <text key={i} x={x + 1} y={y - 1 - (lines.length - 1 - i) * 2.2} fontSize="1.7" fontWeight="700" fill="#0f172a">{s}</text>
        ))}
      </g>
    );
  })();

  switch (m.markup_type) {
    case "pin":
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <circle cx={center.x * 100} cy={center.y * 100} r="1.4" fill={c} stroke="white" strokeWidth="0.35" opacity={opacity}/>
          {labelBlock}
        </g>
      );
    case "rectangle":
    case "highlight": {
      const [a, b] = m.coordinates;
      if (!b) return null;
      const x = Math.min(a.x, b.x) * 100, y = Math.min(a.y, b.y) * 100;
      const w = Math.abs(a.x - b.x) * 100, h = Math.abs(a.y - b.y) * 100;
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <rect x={x} y={y} width={w} height={h}
                fill={m.markup_type === "highlight" ? c : "none"}
                fillOpacity={m.markup_type === "highlight" ? Math.max(fillOpacity, 0.2) : 0}
                stroke={stroke} strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity}/>
          {labelBlock}
        </g>
      );
    }
    case "circle": {
      const [a, b] = m.coordinates;
      if (!b) return null;
      const cx = a.x * 100, cy = a.y * 100;
      const rx = Math.abs(b.x - a.x) * 100;
      const ry = Math.abs(b.y - a.y) * 100;
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
                   fill={c} fillOpacity={fillOpacity}
                   stroke={stroke} strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity}/>
          {labelBlock}
        </g>
      );
    }
    case "polygon":
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <polygon points={pts} fill={c} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity}/>
          {labelBlock}
        </g>
      );
    case "freehand": {
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <polyline points={pts} fill="none" stroke={stroke} strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity} strokeLinecap="round" strokeLinejoin="round"/>
          {labelBlock}
        </g>
      );
    }
    case "line":
    case "measurement":
    case "arrow": {
      const [a, b] = m.coordinates;
      if (!b) return null;
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <line x1={a.x * 100} y1={a.y * 100} x2={b.x * 100} y2={b.y * 100}
                stroke={stroke} strokeWidth={strokeW} strokeDasharray={dash} opacity={opacity}
                markerEnd={m.markup_type === "arrow" ? "url(#arrowHead)" : undefined}/>
          <defs>
            <marker id="arrowHead" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
              <path d="M0,0 L4,2 L0,4 z" fill={stroke}/>
            </marker>
          </defs>
          {labelBlock}
        </g>
      );
    }
    case "text":
      return (
        <g onClick={onClick} style={{ cursor: "pointer" }}>
          <rect x={center.x * 100} y={center.y * 100 - 2.5} rx={0.6} width={Math.max((m.label || "Note").length * 0.9 + 1, 4)} height={3.4} fill={c} opacity={opacity}/>
          <text x={center.x * 100 + 0.5} y={center.y * 100} fontSize="2" fill="white" fontWeight="600">{m.label || "Note"}</text>
        </g>
      );
    default:
      return null;
  }
}

function RenderDraft({ draft }) {
  const stroke = "#0ea5e9", fill = "#0ea5e9";
  const pts = draft.points.map(p => `${p.x * 100},${p.y * 100}`).join(" ");
  if (draft.tool === "polygon") {
    return (
      <g style={{ pointerEvents: "none" }}>
        <polyline points={pts} fill="none" stroke={stroke} strokeDasharray="0.6 0.6" strokeWidth="0.4"/>
        {draft.points.map((p, i) => <circle key={i} cx={p.x * 100} cy={p.y * 100} r="0.7" fill={fill}/>)}
      </g>
    );
  }
  if (draft.tool === "freehand") {
    return <polyline points={pts} fill="none" stroke={stroke} strokeWidth="0.4" style={{ pointerEvents: "none" }} strokeLinecap="round" strokeLinejoin="round"/>;
  }
  if (draft.tool === "set-scale") {
    return (
      <g style={{ pointerEvents: "none" }}>
        {draft.points.length === 2 && <line x1={draft.points[0].x * 100} y1={draft.points[0].y * 100} x2={draft.points[1].x * 100} y2={draft.points[1].y * 100} stroke="#0ea5e9" strokeWidth="0.5" strokeDasharray="0.6 0.6"/>}
        {draft.points.map((p, i) => <circle key={i} cx={p.x * 100} cy={p.y * 100} r="0.9" fill="#0ea5e9" stroke="white" strokeWidth="0.2"/>)}
        {draft.points.length === 1 && (
          <text x={draft.points[0].x * 100 + 1.2} y={draft.points[0].y * 100 - 1} fontSize="1.8" fontWeight="700" fill="#0369a1">Click point B</text>
        )}
      </g>
    );
  }
  if (draft.points.length === 1) {
    return <circle cx={draft.points[0].x * 100} cy={draft.points[0].y * 100} r="0.7" fill={fill}/>;
  }
  return null;
}

// =====================================================================================
// Vertex handles for Edit-Polygon mode (mobile-friendly large hit areas)
// =====================================================================================
function VertexHandles({ markup, onVertexDragStart, onInsertVertex, onDeleteVertex }) {
  const coords = markup.coordinates || [];
  return (
    <g data-testid="polygon-vertex-handles">
      {coords.map((p, i) => {
        const next = coords[(i + 1) % coords.length];
        const mid = { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
        return (
          <g key={i}>
            {/* Edge midpoint insert dot (smaller, semi-transparent) */}
            <circle
              cx={mid.x * 100} cy={mid.y * 100} r="1.0"
              fill="#fef3c7" stroke="#92400e" strokeWidth="0.2" opacity={0.75}
              style={{ cursor: "copy" }}
              onClick={(e) => { e.stopPropagation(); onInsertVertex(markup.id, i); }}
              data-testid={`insert-vertex-${i}`}
            />
            {/* Vertex grab handle (larger, easy tap on mobile ~12-14px effective at 1x zoom) */}
            <circle
              cx={p.x * 100} cy={p.y * 100} r="1.8"
              fill="#0ea5e9" stroke="white" strokeWidth="0.5"
              style={{ cursor: "grab" }}
              onPointerDown={onVertexDragStart(markup.id, i)}
              onDoubleClick={(e) => { e.stopPropagation(); onDeleteVertex(markup.id, i); }}
              data-testid={`vertex-${i}`}
            />
          </g>
        );
      })}
    </g>
  );
}

// =====================================================================================
// Save Markup dialog (existing logic preserved + style fields)
// =====================================================================================
function SaveMarkupDialog({ open, onClose, tool, points, plan, onSaved }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [colour, setColour] = useState("#0369a1");
  const [strokeWidth, setStrokeWidth] = useState(0.4);
  const [fillOpacity, setFillOpacity] = useState(0.18);
  const [lineStyle, setLineStyle] = useState("solid");
  const [linkMode, setLinkMode] = useState("none");
  const [lots, setLots] = useState([]);
  const [itps, setItps] = useState([]);
  const [users, setUsers] = useState([]);
  const [linkedLotId, setLinkedLotId] = useState("");
  const [newLotForm, setNewLotForm] = useState({ lot_number: "", activity: "", itp_template_id: "", location: "", responsible_engineer_id: "" });
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!plan) return;
    api.get("/lots", { params: { project_id: plan.project_id } }).then(r => setLots(r.data));
    api.get("/itp-templates").then(r => setItps(r.data.filter(t => t.status === "Approved")));
    api.get("/users").then(r => setUsers(r.data));
  }, [plan]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        plan_id: plan.id,
        project_id: plan.project_id,
        markup_type: tool,
        coordinates: points,
        label, description, colour,
        stroke_width: Number(strokeWidth),
        fill_opacity: Number(fillOpacity),
        line_style: lineStyle,
        page: 1,
        linked_lot_id: linkMode === "existing" ? linkedLotId : null,
      };
      const { data: markup } = await api.post("/markups", payload);
      if (linkMode === "new") {
        if (!newLotForm.lot_number || !newLotForm.itp_template_id) {
          toast.error("Lot number and ITP required");
          setSaving(false); return;
        }
        const { data: lot } = await api.post(`/markups/${markup.id}/create-lot`, {
          ...newLotForm, location: newLotForm.location || label,
        });
        toast.success(`Lot ${lot.lot_number} created and linked`);
        onSaved();
        nav(`/lots/${lot.id}`);
        return;
      }
      toast.success("Markup saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const showFillControls = ["polygon", "rectangle", "circle", "highlight"].includes(tool);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="save-markup-dialog">
        <DialogHeader><DialogTitle>Save markup</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 p-2 text-xs"><b>Type:</b> {tool} · <b>Points:</b> {points.length}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. AREA-DR-001" data-testid="markup-label"/></div>
            <div><Label>Colour</Label><Input type="color" value={colour} onChange={e => setColour(e.target.value)} className="h-10" data-testid="markup-colour"/></div>
            <div>
              <Label className="text-xs">Stroke width</Label>
              <Input type="number" step="0.1" min="0.1" max="2" value={strokeWidth} onChange={e => setStrokeWidth(e.target.value)} data-testid="markup-stroke-width"/>
            </div>
            <div>
              <Label className="text-xs">Line style</Label>
              <Select value={lineStyle} onValueChange={setLineStyle}>
                <SelectTrigger data-testid="markup-line-style"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="dashed">Dashed</SelectItem>
                  <SelectItem value="dotted">Dotted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showFillControls && (
              <div className="col-span-2">
                <Label className="text-xs">Fill opacity ({Number(fillOpacity).toFixed(2)})</Label>
                <input type="range" min="0" max="1" step="0.05" value={fillOpacity} onChange={e => setFillOpacity(e.target.value)} className="w-full" data-testid="markup-fill-opacity"/>
              </div>
            )}
          </div>
          <div><Label>Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}/></div>
          <div>
            <Label>Link to lot</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {[["none", "No link"], ["existing", "Existing lot"], ["new", "New lot from markup"]].map(([v, l]) => (
                <button key={v} onClick={() => setLinkMode(v)} type="button" data-testid={`link-mode-${v}`}
                  className={`rounded-md border-2 p-2 text-xs font-semibold ${linkMode === v ? "border-teal-600 bg-sky-50 text-teal-800" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
          </div>
          {linkMode === "existing" && (
            <div>
              <Label>Lot</Label>
              <Select value={linkedLotId} onValueChange={setLinkedLotId}>
                <SelectTrigger data-testid="link-existing-lot"><SelectValue placeholder="Pick a lot"/></SelectTrigger>
                <SelectContent>{lots.map(l => <SelectItem key={l.id} value={l.id}>{l.lot_number} · {l.activity}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {linkMode === "new" && (
            <div className="space-y-2 rounded-md border border-slate-200 p-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Lot Number *</Label><Input value={newLotForm.lot_number} onChange={e => setNewLotForm({ ...newLotForm, lot_number: e.target.value })} placeholder={label || "TRIU-DR-002"} data-testid="newlot-number"/></div>
                <div><Label className="text-xs">Activity</Label><Input value={newLotForm.activity} onChange={e => setNewLotForm({ ...newLotForm, activity: e.target.value })}/></div>
                <div className="col-span-2">
                  <Label className="text-xs">ITP *</Label>
                  <Select value={newLotForm.itp_template_id} onValueChange={v => setNewLotForm({ ...newLotForm, itp_template_id: v })}>
                    <SelectTrigger data-testid="newlot-itp"><SelectValue placeholder="Choose ITP"/></SelectTrigger>
                    <SelectContent>{itps.map(t => <SelectItem key={t.id} value={t.id}>{t.itp_number} — {t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs">Location / Chainage</Label><Input value={newLotForm.location} onChange={e => setNewLotForm({ ...newLotForm, location: e.target.value })} placeholder={label}/></div>
                <div className="col-span-2">
                  <Label className="text-xs">Engineer</Label>
                  <Select value={newLotForm.responsible_engineer_id} onValueChange={v => setNewLotForm({ ...newLotForm, responsible_engineer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Assign engineer"/></SelectTrigger>
                    <SelectContent>{users.filter(u => u.role === "site_engineer").map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-markup">
            <Save className="mr-1.5 size-4"/>{saving ? "Saving…" : "Save markup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================================
// Scale Calibration dialog
// =====================================================================================
function ScaleCalibrationDialog({ open, onClose, planId, pointA, pointB, baseDims, onSaved }) {
  const [realDistance, setRealDistance] = useState("");
  const [unit, setUnit] = useState("m");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = parseFloat(realDistance);
    if (!n || n <= 0) { toast.error("Enter a real-world distance"); return; }
    if (!baseDims || !baseDims.w || !baseDims.h) { toast.error("Plan not yet rendered — try again in a moment"); return; }
    setSaving(true);
    try {
      await api.post(`/plans/${planId}/calibrate-scale`, {
        point_a: pointA, point_b: pointB,
        real_distance: n, unit,
        base_width: baseDims.w, base_height: baseDims.h,
      });
      toast.success("Scale calibrated. Polygon areas will now use this scale.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to calibrate scale");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="scale-calibration-dialog">
        <DialogHeader><DialogTitle>Calibrate drawing scale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-600">
            You picked two reference points on the drawing.
            Enter the real-world distance between them. All polygon areas on this
            drawing will be recomputed using this scale.
          </p>
          <div className="rounded-md bg-slate-50 p-2 text-xs">
            <div><b>Point A:</b> ({pointA.x.toFixed(3)}, {pointA.y.toFixed(3)})</div>
            <div><b>Point B:</b> ({pointB.x.toFixed(3)}, {pointB.y.toFixed(3)})</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Real-world distance *</Label>
              <Input
                type="number" step="0.001" min="0.001"
                value={realDistance} onChange={e => setRealDistance(e.target.value)}
                placeholder="e.g. 10"
                data-testid="scale-real-distance"
                autoFocus
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="scale-unit"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="m">metres (m)</SelectItem>
                  <SelectItem value="mm">millimetres (mm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">Areas are always shown in m² regardless of the unit chosen.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-scale-calibration">
            <Ruler className="mr-1.5 size-4"/>{saving ? "Saving…" : "Save scale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================================
// Superseded warning modal
// =====================================================================================
function SupersededWarningModal({ open, onClose, plan }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="superseded-warning-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="size-5"/>This drawing is marked as Superseded
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm text-slate-700">
          <p>{plan?.drawing_number} Rev {plan?.revision} — {plan?.title}</p>
          <p>This drawing has been superseded by a newer revision. Confirm before
            using it for site work — markup actions are still permitted for
            historical reference only.</p>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-rose-700 hover:bg-rose-800" data-testid="superseded-acknowledge">I understand, continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================================
// Sidebar
// =====================================================================================
function SidebarPanel({ selected, setSelected, markups, linkedLots, plan, reloadMarkups, onDelete, editingPolyId, setEditingPolyId, setTool }) {
  const [linkedLot, setLinkedLot] = useState(null);
  useEffect(() => {
    if (selected?.linked_lot_id) {
      api.get(`/lots/${selected.linked_lot_id}`).then(r => setLinkedLot(r.data)).catch(() => setLinkedLot(null));
    } else setLinkedLot(null);
  }, [selected]);

  const totalVisibleArea = markups
    .filter(m => m.markup_type === "polygon" && m.area_m2)
    .reduce((sum, m) => sum + Number(m.area_m2 || 0), 0);

  if (selected) {
    return (
      <div className="space-y-3" data-testid="sidebar-selected">
        <div className="flex items-center justify-between">
          <div className="overline">Selected markup</div>
          <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900"><X className="size-4"/></button>
        </div>
        <div className="rounded-md border border-slate-200 p-3">
          <div className="font-heading text-base font-bold text-slate-900">{selected.label || "(no label)"}</div>
          <div className="text-xs text-slate-600">{selected.markup_type} · {selected.coordinates?.length} pts</div>
          {selected.markup_type === "polygon" && (
            <div className="mt-1 rounded bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-900" data-testid="polygon-area-display">
              Area: {fmtArea(selected.area_m2) || "(scale not set)"}
            </div>
          )}
          {selected.description && <p className="mt-1 text-xs text-slate-600">{selected.description}</p>}
          {selected.created_by_name && (
            <div className="mt-1 text-[11px] text-slate-500">
              by {selected.created_by_name} · {(selected.created_at || "").slice(0, 10)}
              {selected.updated_at && <> · edited {selected.updated_at.slice(0, 10)}</>}
            </div>
          )}
          {selected.needs_verification && <div className="mt-1 rounded bg-orange-50 px-2 py-1 text-[11px] text-orange-700">Copied from previous revision — verify against this drawing.</div>}
        </div>

        {selected.markup_type === "polygon" && (
          <Button
            size="sm"
            variant={editingPolyId === selected.id ? "default" : "outline"}
            className={`w-full ${editingPolyId === selected.id ? "bg-sky-600 hover:bg-sky-700" : ""}`}
            onClick={() => {
              if (editingPolyId === selected.id) { setEditingPolyId(null); setTool("select"); }
              else { setEditingPolyId(selected.id); setTool("edit-polygon"); }
            }}
            data-testid="toggle-edit-polygon"
          >
            <Move3D className="mr-1.5 size-4"/>{editingPolyId === selected.id ? "Done editing" : "Edit polygon vertices"}
          </Button>
        )}

        {linkedLot ? (
          <Card className="border-slate-200 p-3" data-testid="sidebar-lot">
            <div className="overline">Linked lot</div>
            <div className="font-mono font-semibold">{linkedLot.lot_number}</div>
            <div className="text-xs">{linkedLot.activity}</div>
            <div className="mt-1"><StatusBadge status={linkedLot.status}/></div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link to={`/lots/${linkedLot.id}`}><Button size="sm" className="w-full bg-teal-700 hover:bg-teal-800" data-testid="sidebar-open-lot">Open Lot</Button></Link>
              <Link to={`/lots/${linkedLot.id}#evidence`}><Button size="sm" variant="outline" className="w-full">Upload Evidence</Button></Link>
            </div>
          </Card>
        ) : null}

        <Button size="sm" variant="outline" className="w-full text-red-700 hover:bg-red-50" onClick={() => onDelete(selected.id)} data-testid="delete-markup">
          <Trash2 className="mr-1.5 size-4"/>Delete markup
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sidebar-default">
      <div>
        <div className="overline">Plan info</div>
        <div className="mt-1 text-sm font-semibold">{plan.title}</div>
        <div className="text-xs text-slate-500">{plan.drawing_number} Rev {plan.revision} · {plan.category || plan.discipline}</div>
        {plan.scale_calibration && (
          <div className="mt-1 text-[11px] text-emerald-700">
            Scale: 1 m ≈ {Number(plan.scale_calibration.pixels_per_meter).toFixed(1)} px
          </div>
        )}
      </div>
      {totalVisibleArea > 0 && (
        <div className="rounded-md bg-emerald-50 p-2 text-xs" data-testid="total-area-display">
          <div className="font-semibold text-emerald-900">Total polygon area</div>
          <div className="text-lg font-bold text-emerald-800">{fmtArea(totalVisibleArea)}</div>
        </div>
      )}
      <div>
        <div className="overline">All markups ({markups.length})</div>
        {markups.length === 0 && <div className="text-xs text-slate-500">None yet — pick a tool and click on the plan.</div>}
        <div className="mt-1 space-y-1">
          {markups.map(m => (
            <button key={m.id} onClick={() => setSelected(m)} className="flex w-full items-center justify-between gap-1 rounded border border-slate-200 px-2 py-1 text-left text-xs hover:bg-slate-50" data-testid={`markup-listitem-${m.id}`}>
              <span className="flex items-center gap-2">
                <span className="inline-block size-2 rounded-full" style={{ background: m.colour }}/>
                <span className="font-semibold text-slate-900">{m.label || `(${m.markup_type})`}</span>
              </span>
              <span className="text-[10px] uppercase text-slate-500">
                {m.markup_type === "polygon" && m.area_m2 ? fmtArea(m.area_m2) : m.markup_type}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="overline">Linked lots ({linkedLots.length})</div>
        <div className="mt-1 space-y-1">
          {linkedLots.map(l => (
            <Card key={l.id} className="border-slate-200 p-2 text-xs" data-testid={`linkedlot-${l.lot_number}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold">{l.lot_number}</span>
                <StatusBadge status={l.status}/>
              </div>
              <div className="mt-0.5 text-slate-600">{l.activity}</div>
              <Link to={`/lots/${l.id}`} className="mt-1 inline-block font-semibold text-teal-700 hover:underline">Open lot →</Link>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================================
// Mobile properties bottom sheet
// =====================================================================================
function MobilePropertiesSheet({ selected, setSelected, plan, onClose, onDelete, onEdit }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-slate-200 bg-white p-4 shadow-2xl lg:hidden" data-testid="mobile-properties-sheet">
      <div className="mb-2 flex items-center justify-between">
        <div className="overline">Markup</div>
        <button onClick={onClose} className="text-slate-500" data-testid="close-mobile-sheet"><X className="size-5"/></button>
      </div>
      <div className="space-y-1.5">
        <div className="font-heading text-base font-bold text-slate-900">{selected.label || "(no label)"}</div>
        <div className="text-xs text-slate-600">{selected.markup_type} · {selected.coordinates?.length} pts</div>
        {selected.markup_type === "polygon" && (
          <div className="rounded bg-emerald-50 px-2 py-1 text-sm font-semibold text-emerald-900">
            Area: {fmtArea(selected.area_m2) || "(scale not set)"}
          </div>
        )}
        {selected.created_by_name && (
          <div className="text-[11px] text-slate-500">
            by {selected.created_by_name} · {(selected.created_at || "").slice(0, 10)}
          </div>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {selected.markup_type === "polygon" && (
          <Button size="sm" onClick={onEdit} className="bg-sky-600 hover:bg-sky-700" data-testid="mobile-edit-polygon">
            <Move3D className="mr-1 size-4"/>Edit vertices
          </Button>
        )}
        <Button size="sm" variant="outline" className="text-red-700 hover:bg-red-50" onClick={onDelete} data-testid="mobile-delete-markup">
          <Trash2 className="mr-1 size-4"/>Delete
        </Button>
      </div>
    </div>
  );
}
